import React, { useRef, useState } from "react";
import { UploadCloud, X, Loader2 } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { toast } from "sonner";

// Drag-and-drop media picker. Value is a URL string (external or /api/media/file/id).
export function MediaPicker({ value, onChange, label, accept = "image/*", kind = "image", recommended, testid = "media-picker" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/media/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Uploaded");
    } catch (e) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid={testid}>
      {label && <label className="block text-xs uppercase tracking-[0.14em] text-taupe mb-2">{label}</label>}
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files?.[0]); }}
        onClick={() => inputRef.current?.click()}
        className={`relative cursor-pointer rounded-md border-2 border-dashed overflow-hidden transition-colors ${drag ? "border-brass bg-brass/5" : "border-espresso/20 hover:border-espresso/40 bg-ivory"}`}
      >
        {value ? (
          <div className="relative">
            {kind === "video" ? (
              <video src={resolveImg(value)} className="w-full h-44 object-cover" muted />
            ) : (
              <img src={resolveImg(value)} alt="preview" className="w-full h-44 object-cover" />
            )}
            <button type="button" onClick={(e) => { e.stopPropagation(); onChange(""); }}
              className="absolute top-2 right-2 bg-espresso/80 text-ivory rounded-full p-1.5" data-testid={`${testid}-clear`}>
              <X size={14} />
            </button>
          </div>
        ) : (
          <div className="h-44 flex flex-col items-center justify-center text-taupe gap-2">
            {uploading ? <Loader2 className="animate-spin" /> : <UploadCloud size={28} strokeWidth={1.4} />}
            <span className="text-sm">{uploading ? "Uploading…" : "Drag & drop or click to upload"}</span>
          </div>
        )}
      </div>
      <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`${testid}-input`} />
      <div className="flex items-center justify-between mt-2">
        {recommended && <span className="text-[0.68rem] text-taupe">Recommended: {recommended}</span>}
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="or paste an image URL"
          className="text-xs bg-transparent border-b border-espresso/15 focus:outline-none focus:border-brass py-1 flex-1 ml-4 text-espressosoft" />
      </div>
    </div>
  );
}
